<script setup>
import { ref, watch, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import useDialog from '@/composables/use-dialog';
import useCustomForm from '@/composables/use-CustomForm';
import CreationDialog from '@/components/widgets/creationDialog/CreationDialog.vue';
import CustomerSummary from '@/components/Customer/CustomerSummary.vue';
import customerAddressDialogTemplate from '@/config/templates/customerAddressDialog.js';
import FormSectionTitle from '@/components/widgets/forms/FormSectionTitle.vue';
import FormLine from '@/components/widgets/creationDialog/FormLine.vue';

const { t } = useI18n({ useScope: 'global' });

const { show, close } = useDialog();

defineProps(['title']);
const emit = defineEmits(['saved', 'update', 'update:modelValue']);
const backendApi = inject('backendApi');

const { fieldModels, onValidatedSubmit, clearForm, updatedFromResponse } =
    useCustomForm(customerAddressDialogTemplate);

const sectionVisibilities = ref(customerAddressDialogTemplate.map(() => true));

const customerData = ref(null);
const loading = ref(false);
const displayErrorMessage = ref(false);
const errorMessage = ref(null);
const isEditMode = ref(false);
const addressId = ref(null);

watch(show, (value) => {
    clearForm();
    if (!value) {
        customerData.value = null;
        displayErrorMessage.value = false;
        errorMessage.value = null;
        isEditMode.value = false;
        addressId.value = null;
    }
});

const fetch = (id) => {
    loading.value = true;
    backendApi()
        .customers()
        .getAddress(id)
        .then((response) => {
            const address = response.data.adrdefault;
            if (address) {
                isEditMode.value = true;
                addressId.value = address.id;
                updatedFromResponse(address);

                fieldModels['state'].value.value = address.city?.state?.name;
                fieldModels['country'].value.value =
                    address.city?.state?.country?.name;
            } else {
                isEditMode.value = false;
                addressId.value = null;
            }
        })
        .catch((error) => {
            displayErrorMessage.value = true;
            errorMessage.value =
                error.data?.message || t('Error loading address');
        })
        .finally(() => {
            loading.value = false;
        });
};

const onFieldChanged = (modelName, value) => {
    if (modelName === 'city') {
        const fieldValue = value;
        fieldModels['state'].value.value = fieldValue?.state?.name;
        fieldModels['country'].value.value = fieldValue?.state?.country?.name;
    }
};

const createAddress = (options, clearFormAfterSaved = false) => {
    backendApi()
        .customers()
        .createAddress({
            customerId: customerData.value.id,
            name: options.name,
            lastName: options.lastname,
            docid: options.docid,
            phone: options.phone,
            mobile: options.mobile,
            email: options.email,
            emailAddresses: options.emailAddresses,
            address: options.address,
            city: options.city?.id,
            zip: options.zip
        })
        .then((response) => {
            emit('saved', response.data);
            emit('update', response.data);

            if (!clearFormAfterSaved) {
                close();
            }
        })
        .catch((error) => {
            displayErrorMessage.value = true;
            errorMessage.value =
                error.data?.message || t('Error creating address');
        })
        .finally(() => {
            loading.value = false;
        });
};

const updateAddress = (options, clearFormAfterSaved = false) => {
    backendApi()
        .customers()
        .updateAddress({
            id: addressId.value,
            customerId: customerData.value.id,
            name: options.name,
            lastName: options.lastname,
            docid: options.docid,
            phone: options.phone,
            mobile: options.mobile,
            email: options.email,
            emailAddresses: options.emailAddresses,
            address: options.address,
            city: options.city?.id,
            zip: options.zip
        })
        .then((response) => {
            emit('update', response.data);
            emit('update:modelValue', response.data);

            if (!clearFormAfterSaved) {
                close();
            }
        })
        .catch((error) => {
            displayErrorMessage.value = true;
            errorMessage.value = error.data?.message;
        })
        .finally(() => {
            loading.value = false;
        });
};

const open = async (data, mode, parameters) => {
    show.value = true;

    if (parameters) {
        customerData.value = parameters.customer || parameters;
    }

    if (data && data.id) {
        fetch(data.id);
    } else {
        isEditMode.value = false;
        addressId.value = null;
    }
};

const onSaveBtnPressed = () => {
    onValidatedSubmit((options) => {
        loading.value = true;
        if (isEditMode.value) {
            updateAddress(options);
        } else {
            createAddress(options);
        }
    });
};

const onNewBtnPressed = () => {
    onValidatedSubmit((options) => {
        loading.value = true;
        if (isEditMode.value) {
            updateAddress(options, true);
        } else {
            createAddress(options, true);
        }
    });
};

const onRetryBtnPressed = () => {
    displayErrorMessage.value = false;
    onSaveBtnPressed();
};

defineExpose({
    open,
    close
});
</script>

<template>
    <CreationDialog
        v-model="show"
        :page="t('Customer')"
        :title="isEditMode ? t('Edit Address') : t('Create Address')"
        :width="1000"
        :compact-header-margin="true"
        :hideEditAction="true"
        :hideNewAction="true"
        :error-message="errorMessage"
        :hideRemoveAction="true"
        :saving="loading"
        @save="onSaveBtnPressed"
        @new="onNewBtnPressed"
        @retry="onRetryBtnPressed"
    >
        <template v-slot:summary>
            <CustomerSummary :customer-data="customerData" :show-phone="true" />
        </template>

        <template v-slot:form>
            <section
                v-for="(section, index) in customerAddressDialogTemplate"
                :key="section.title"
            >
                <FormSectionTitle
                    :title="t(section.title)"
                    v-model="sectionVisibilities[index]"
                />
                <transition name="slide">
                    <v-row v-show="sectionVisibilities[index]" class="mt-3">
                        <FormLine
                            v-for="field of section.fields"
                            :key="field.label"
                            :fullWidth="field.fullWidth"
                            :oneColumn="field.oneColumn"
                            :label="field.label"
                            :mode="field.disabled ? '' : 'edit'"
                            :component="field.component"
                            :fieldModels="fieldModels"
                            :error-messages="
                                fieldModels[field.model].errorMessage.value
                            "
                            v-model="fieldModels[field.model].value.value"
                            :required="field.required"
                            :model-name="field.model"
                            :align="field.align"
                            :return-object="field.returnObject"
                            :item-title="field.itemTitle"
                            :item-value="field.itemValue"
                            @update:modelValue="
                                (value) => onFieldChanged(field.model, value)
                            "
                        />
                    </v-row>
                </transition>
            </section>
        </template>
    </CreationDialog>
</template>

<style lang="scss" scoped>
section {
    margin-bottom: 3rem;
}
</style>
